import * as React from 'react'
import { Award, Building2, Check, Mail, Plus, Shield, SlidersHorizontal, Trash2, UserPlus, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import type { Certification, MemberRole, Team, TeamMember } from '@/lib/types'
import { featureOn, TEAM_FEATURES } from '@/lib/features'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { TeamDialog } from '@/components/team-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

const ROLES: Array<{ value: MemberRole; label: string; hint: string }> = [
  { value: 'member', label: 'Member', hint: 'Reads and contributes to everything on the team.' },
  { value: 'team_lead', label: 'Team lead', hint: 'Also defines metrics and manages the roster.' },
  { value: 'dpl_coach', label: 'DPL coach', hint: 'Don’t Panic Labs coach embedded on this team.' },
]

export function AdminPage() {
  const { refresh, session } = useAuth()
  const [creatingTeam, setCreatingTeam] = React.useState(false)
  const [editingTeam, setEditingTeam] = React.useState<Team | null>(null)
  const [invitingTo, setInvitingTo] = React.useState<Team | null>(null)
  const [certsFor, setCertsFor] = React.useState<Team | null>(null)
  const [featuresFor, setFeaturesFor] = React.useState<Team | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    const [teams, members] = await Promise.all([
      sb().from('teams').select('*').order('name'),
      sb().from('team_members').select('*').order('email'),
    ])
    if (teams.error) throw teams.error
    if (members.error) throw members.error
    return {
      teams: (teams.data ?? []) as Team[],
      members: (members.data ?? []) as TeamMember[],
    }
  }, [])

  // The team switcher and permission checks read from AuthProvider, so any
  // roster change has to refresh that too — otherwise an admin adds themselves
  // to a new team and the switcher doesn't show it until a reload.
  const reload = React.useCallback(async () => {
    await refetch()
    await refresh()
  }, [refetch, refresh])

  async function setRole(member: TeamMember, role: MemberRole) {
    const { error: err } = await sb().from('team_members').update({ role }).eq('id', member.id)
    if (err) return window.alert(err.message)
    void reload()
  }

  async function setActive(member: TeamMember, active: boolean) {
    const { error: err } = await sb().from('team_members').update({ active }).eq('id', member.id)
    if (err) return window.alert(err.message)
    void reload()
  }

  async function removeMember(member: TeamMember) {
    if (!window.confirm(`Remove ${member.email} from this team?`)) return
    const { error: err } = await sb().from('team_members').delete().eq('id', member.id)
    if (err) return window.alert(err.message)
    void reload()
  }

  async function removeTeam(team: Team) {
    const count = (data?.members ?? []).filter((m) => m.team_id === team.id).length
    if (
      !window.confirm(
        `Delete “${team.name}”?\n\nThis permanently removes its ${count} member${count === 1 ? '' : 's'}, ` +
          `metrics, topics, evaluations, workshops, conversation, and boards.`,
      )
    ) {
      return
    }
    const { error: err } = await sb().from('teams').delete().eq('id', team.id)
    if (err) return window.alert(err.message)
    void reload()
  }

  return (
    <Page
      title="Admin"
      description="Teams and membership. Don’t Panic Labs staff only."
      actions={
        <Button onClick={() => setCreatingTeam(true)}>
          <Plus /> New team
        </Button>
      }
    >
      {error && <ErrorNote error={error} />}

      <Card className="mb-6 border-primary/30 bg-primary/5 shadow-none">
        <CardContent className="flex items-start gap-3 py-4">
          <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-medium">How access works</p>
            <p className="mt-0.5 text-muted-foreground">
              Invite someone by email and they can sign in with a magic link — no account setup, no
              password. The invitation is claimed automatically the first time they sign in with that
              address. Anyone with an <span className="font-medium text-foreground">@dontpaniclabs.com</span>{' '}
              address becomes a DPL admin with access to every team.
            </p>
          </div>
        </CardContent>
      </Card>

      {loading && !data ? (
        <Loading />
      ) : (data?.teams.length ?? 0) === 0 ? (
        <EmptyState
          icon={Building2}
          title="No teams yet"
          description="Create a team for a partner, then invite their engineers by email."
          action={
            <Button onClick={() => setCreatingTeam(true)}>
              <Plus /> Create the first team
            </Button>
          }
        />
      ) : (
        <div className="space-y-5">
          {data?.teams.map((team) => {
            const roster = (data?.members ?? []).filter((m) => m.team_id === team.id)
            const pending = roster.filter((m) => !m.user_id).length
            return (
              <Card key={team.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                        {team.name}
                        <Badge
                          variant={team.status === 'active' ? 'success' : team.status === 'paused' ? 'warning' : 'muted'}
                          className="text-[10px]"
                        >
                          {team.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="mt-0.5">
                        {[
                          team.company,
                          `/${team.slug}`,
                          team.started_on ? `started ${formatDate(team.started_on)}` : null,
                          `${team.sprint_length_days}-day sprints`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </CardDescription>
                      {team.description && (
                        <p className="mt-1.5 text-sm text-muted-foreground">{team.description}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => setInvitingTo(team)}>
                        <UserPlus /> Invite
                      </Button>
                      {/* Off by default, per team. The button says the state so
                          an admin does not have to open the dialog to read it. */}
                      <Button
                        variant={team.certifications_enabled ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setCertsFor(team)}
                      >
                        <Award /> {team.certifications_enabled ? 'Programs on' : 'Programs'}
                      </Button>
                      {/* The count is the useful part at a glance: "6 of 6"
                          means nothing has been switched off, so an admin only
                          opens this when it says otherwise. */}
                      <Button variant="ghost" size="sm" onClick={() => setFeaturesFor(team)}>
                        <SlidersHorizontal />{' '}
                        {TEAM_FEATURES.filter((f) => featureOn(team, f.key)).length} of{' '}
                        {TEAM_FEATURES.length}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingTeam(team)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => void removeTeam(team)}
                        aria-label={`Delete ${team.name}`}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <Separator className="mb-3" />
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {roster.length} {roster.length === 1 ? 'member' : 'members'}
                    {pending > 0 && (
                      <Badge variant="muted" className="text-[10px]">
                        {pending} not signed in yet
                      </Badge>
                    )}
                  </div>

                  {roster.length === 0 ? (
                    <p className="py-2 text-sm text-muted-foreground">
                      Nobody yet.{' '}
                      <button className="font-medium text-primary hover:underline" onClick={() => setInvitingTo(team)}>
                        Invite the first person
                      </button>
                      .
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px] text-sm">
                        <thead>
                          <tr className="border-b text-left text-xs text-muted-foreground">
                            <th className="pb-2 pr-3 font-medium">Person</th>
                            <th className="pb-2 px-3 font-medium">Status</th>
                            <th className="pb-2 px-3 font-medium">Role</th>
                            <th className="pb-2 pl-3" />
                          </tr>
                        </thead>
                        <tbody>
                          {roster.map((m) => {
                            const isSelf = m.user_id && m.user_id === session?.user?.id
                            return (
                              <tr key={m.id} className={cn('border-b last:border-0', !m.active && 'opacity-55')}>
                                <td className="py-2 pr-3">
                                  <div className="font-medium">
                                    {m.display_name || m.email.split('@')[0]}
                                    {isSelf && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}
                                  </div>
                                  <div className="text-xs text-muted-foreground">{m.email}</div>
                                </td>
                                <td className="py-2 px-3">
                                  {!m.user_id ? (
                                    <Badge variant="outline" className="gap-1 text-[10px]">
                                      <Mail className="h-3 w-3" /> invited
                                    </Badge>
                                  ) : !m.active ? (
                                    <Badge variant="muted" className="text-[10px]">
                                      inactive
                                    </Badge>
                                  ) : (
                                    <Badge variant="success" className="text-[10px]">
                                      active
                                    </Badge>
                                  )}
                                  {m.joined_at && (
                                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                                      joined {formatDate(m.joined_at)}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  <Select value={m.role} onValueChange={(v) => void setRole(m, v as MemberRole)}>
                                    <SelectTrigger className="h-8 w-[136px] text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {ROLES.map((r) => (
                                        <SelectItem key={r.value} value={r.value}>
                                          {r.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="py-2 pl-3 text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => void setActive(m, !m.active)}
                                  >
                                    {m.active ? 'Deactivate' : 'Reactivate'}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => void removeMember(m)}
                                    aria-label={`Remove ${m.email}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {(creatingTeam || editingTeam) && (
        <TeamDialog
          team={editingTeam ?? undefined}
          onClose={() => {
            setCreatingTeam(false)
            setEditingTeam(null)
          }}
          onSaved={() => {
            setCreatingTeam(false)
            setEditingTeam(null)
            void reload()
          }}
        />
      )}

      {certsFor && (
        <CertificationsDialog
          team={certsFor}
          onClose={() => setCertsFor(null)}
          onSaved={() => {
            setCertsFor(null)
            void refetch()
            void refresh()
          }}
        />
      )}

      {featuresFor && (
        <FeaturesDialog
          team={featuresFor}
          onClose={() => setFeaturesFor(null)}
          onSaved={() => {
            setFeaturesFor(null)
            void refetch()
            // Refresh the session too: the sidebar and the route table read
            // these flags off the team in AuthProvider, so without this the
            // admin saves and nothing visibly changes until a reload.
            void refresh()
          }}
        />
      )}

      {invitingTo && (
        <InviteDialog
          team={invitingTo}
          existing={(data?.members ?? []).filter((m) => m.team_id === invitingTo.id)}
          onClose={() => setInvitingTo(null)}
          onSaved={() => {
            setInvitingTo(null)
            void reload()
          }}
        />
      )}
    </Page>
  )
}

/**
 * Invite by email. Deliberately does NOT send anything: there's no user to
 * create and no password to set, so the invitation is just a row. The person
 * signs in with a magic link whenever they like and the trigger on auth.users
 * claims the row by matching their email.
 */
function InviteDialog({
  team,
  existing,
  onClose,
  onSaved,
}: {
  team: Team
  existing: TeamMember[]
  onClose: () => void
  onSaved: () => void
}) {
  const [raw, setRaw] = React.useState('')
  const [role, setRole] = React.useState<MemberRole>('member')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [result, setResult] = React.useState<string | null>(null)

  // A pasted list of twenty addresses is exactly the thing not to lose to a
  // stray click on the overlay.
  const guard = useDiscardGuard(useDirty({ raw, role }), onClose)

  // Accept a pasted list — one per line, or comma/semicolon separated, and
  // tolerate "Name <email@host>" from a mail client.
  const parsed = React.useMemo(() => {
    const tokens = raw
      .split(/[\n,;]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => {
        const angle = t.match(/<([^>]+)>/)
        const email = (angle ? angle[1] : t).trim().toLowerCase()
        const name = angle ? t.slice(0, t.indexOf('<')).trim().replace(/^"|"$/g, '') : ''
        return { email, name }
      })
    const seen = new Set<string>()
    return tokens.filter((t) => {
      if (seen.has(t.email)) return false
      seen.add(t.email)
      return true
    })
  }, [raw])

  const invalid = parsed.filter((p) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email))
  const alreadyOn = parsed.filter((p) => existing.some((m) => m.email.toLowerCase() === p.email))
  const toAdd = parsed.filter((p) => !invalid.includes(p) && !alreadyOn.includes(p))

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (invalid.length > 0) return setError(`Not a valid email: ${invalid.map((i) => i.email).join(', ')}`)
    if (toAdd.length === 0) return setError('Nobody new to add.')

    setBusy(true)
    setError(null)
    try {
      const { error: err } = await sb()
        .from('team_members')
        .insert(
          toAdd.map((p) => ({
            team_id: team.id,
            email: p.email,
            display_name: p.name || null,
            role,
          })),
        )
      if (err) throw err
      setResult(`Invited ${toAdd.length} ${toAdd.length === 1 ? 'person' : 'people'}.`)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-md">
        <DiscardPrompt guard={guard} what="this invitation list" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>Invite to {team.name}</DialogTitle>
            <DialogDescription>
              One email per line. They&apos;ll get access the first time they sign in with that
              address — nothing is emailed from here.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emails">Email addresses</Label>
              <Textarea
                id="emails"
                autoFocus
                rows={4}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={'ada@acme.com\ngrace@acme.com'}
                className="font-mono text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="irole">Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as MemberRole)}>
                <SelectTrigger id="irole">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {ROLES.find((r) => r.value === role)?.hint}
              </p>
            </div>

            {toAdd.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Will add {toAdd.length}: {toAdd.map((t) => t.email).join(', ')}
              </p>
            )}
            {alreadyOn.length > 0 && (
              <p className="text-xs text-warning">
                Already on this team, skipping: {alreadyOn.map((t) => t.email).join(', ')}
              </p>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            {result && <p className="text-sm text-better">{result}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || toAdd.length === 0}>
              Invite {toAdd.length > 0 ? toAdd.length : ''}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}


/** A chosen set as one comparable string. Sorted, so toggling a path off and
 *  back on is not a change. */
function pickedKey(ids: Set<string>): string {
  return [...ids].sort().join(',')
}

/**
 * Turn programs on for a team, and choose which ones it runs.
 *
 * "Programs" is the label only — the table, the columns and the routes still
 * say certification (see the note in components/app-shell.tsx).
 *
 * The two writes are ordered deliberately. 0037 carries a deferred constraint
 * trigger saying "enabled implies at least one certification", and PostgREST
 * sends each request as its own transaction — so the rows go in BEFORE the flag
 * is raised, and the flag comes DOWN before the last row is removed. Reverse
 * either and the database refuses the half-done state, correctly and
 * unhelpfully.
 */
function CertificationsDialog({
  team,
  onClose,
  onSaved,
}: {
  team: Team
  onClose: () => void
  onSaved: () => void
}) {
  const [catalog, setCatalog] = React.useState<Certification[]>([])
  const [picked, setPicked] = React.useState<Set<string>>(new Set())
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // What was enabled when the list arrived. `useDirty` cannot serve here: its
  // baseline is the first render, which is the empty set this dialog shows
  // while it is still loading — so every path would read as newly chosen.
  const baseline = React.useRef('')
  const guard = useDiscardGuard(!loading && pickedKey(picked) !== baseline.current, onClose)

  React.useEffect(() => {
    let cancelled = false
    void (async () => {
      const [certs, enabled] = await Promise.all([
        sb().from('certifications').select('*').eq('active', true).order('display_order'),
        sb().from('team_certifications').select('certification_id').eq('team_id', team.id),
      ])
      if (cancelled) return
      if (certs.error || enabled.error) {
        setError((certs.error ?? enabled.error)!.message)
      } else {
        setCatalog((certs.data ?? []) as Certification[])
        const was = new Set((enabled.data ?? []).map((r) => r.certification_id as string))
        baseline.current = pickedKey(was)
        setPicked(was)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [team.id])

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const want = [...picked]
      const enable = want.length > 0

      // Flag down first when switching off, so removing the last row is legal.
      if (!enable && team.certifications_enabled) {
        const { error: err } = await sb()
          .from('teams')
          .update({ certifications_enabled: false })
          .eq('id', team.id)
        if (err) throw err
      }

      const current = await sb()
        .from('team_certifications')
        .select('certification_id')
        .eq('team_id', team.id)
      if (current.error) throw current.error
      const have = new Set((current.data ?? []).map((r) => r.certification_id as string))

      const toAdd = want.filter((id) => !have.has(id))
      const toRemove = [...have].filter((id) => !picked.has(id))

      if (toAdd.length > 0) {
        const { error: err } = await sb()
          .from('team_certifications')
          .insert(toAdd.map((certification_id) => ({ team_id: team.id, certification_id })))
        if (err) throw err
      }
      if (toRemove.length > 0) {
        const { error: err } = await sb()
          .from('team_certifications')
          .delete()
          .eq('team_id', team.id)
          .in('certification_id', toRemove)
        if (err) throw err
      }

      // Flag up last when switching on, once the rows exist.
      if (enable && !team.certifications_enabled) {
        const { error: err } = await sb()
          .from('teams')
          .update({ certifications_enabled: true })
          .eq('id', team.id)
        if (err) throw err
      }

      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="these certification paths" />
        <DialogHeader>
          <DialogTitle>Programs for {team.name}</DialogTitle>
          <DialogDescription>
            Off by default. Choosing at least one turns the tab on for everyone on this team;
            choosing none turns it off again.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading the catalog…</p>
          ) : catalog.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No programs in the catalog yet.
            </p>
          ) : (
            catalog.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                  picked.has(c.id) ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    picked.has(c.id) && 'border-primary bg-primary text-primary-foreground',
                  )}
                >
                  {picked.has(c.id) && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{c.title}</span>
                  {c.summary && (
                    <span className="block text-xs text-muted-foreground">{c.summary}</span>
                  )}
                </span>
              </button>
            ))
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={guard.requestClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={busy || loading}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Which sections of the portal this team uses (migrations/0044_team_features.sql).
 *
 * Every switch is ON by default, so this dialog is about turning things OFF —
 * which is why the copy leads with what disappears rather than what appears.
 *
 * One save writes all six. The alternative, a toggle that PATCHes on every
 * click, makes a half-configured team a state the admin can leave by closing
 * the tab, and gives no moment at which to say what is about to happen.
 */
function FeaturesDialog({
  team,
  onClose,
  onSaved,
}: {
  team: Team
  onClose: () => void
  onSaved: () => void
}) {
  const [on, setOn] = React.useState<Record<string, boolean>>(() =>
    // featureOn, not `!== false`: a feature that defaults OFF (Games) must
    // open this dialog unticked, and an undefined column is not an "on".
    Object.fromEntries(TEAM_FEATURES.map((f) => [f.key, featureOn(team, f.key)])),
  )
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const guard = useDiscardGuard(useDirty(on), onClose)

  const turningOff = TEAM_FEATURES.filter((f) => !on[f.key] && featureOn(team, f.key))

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const patch = Object.fromEntries(TEAM_FEATURES.map((f) => [f.key, on[f.key]]))
      const { error: err } = await sb().from('teams').update(patch).eq('id', team.id)
      if (err) throw err
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="these section changes" />
        <DialogHeader>
          <DialogTitle>Sections for {team.name}</DialogTitle>
          <DialogDescription>
            Switch off the parts of the portal this team does not use. Nothing is deleted —
            turn a section back on and its content is where it was.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-1">
          {TEAM_FEATURES.map((f) => {
            const active = on[f.key]
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setOn((prev) => ({ ...prev, [f.key]: !prev[f.key] }))}
                aria-pressed={active}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                  active ? 'border-primary/40 bg-primary/5' : 'border-transparent hover:bg-muted',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    active ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {active && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{f.label}</span>
                  <span className="block text-xs text-muted-foreground">{f.description}</span>
                </span>
              </button>
            )
          })}
        </div>

        {/* Named, not counted. "Workshops and Discussion will disappear" is a
            sentence an admin can check against what they meant to do; "2
            sections" is not. */}
        {turningOff.length > 0 && (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs">
            {turningOff.map((f) => f.label).join(' and ')}{' '}
            {turningOff.length === 1 ? 'disappears' : 'disappear'} from the sidebar for everyone on
            this team. The content is kept.
          </p>
        )}

        {error && <ErrorNote error={error} />}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={guard.requestClose}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void save()} disabled={busy}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
